import { Button, ErrorBoundary, FieldFooter } from "@prismicio/editor-ui"
import { IntegrationField as IntegrationFieldT } from "@prismicio/types-internal/lib/customtypes"
import { Suspense } from "react"

import { useEditorConfig } from "../../EditorConfig"
import { FieldTextInput } from "../FieldTextInput"
import { getFieldPlaceholder } from "../utils"
import {
  type IntegrationItem,
  useIntegrationCatalogRead,
} from "./integrationData"
import {
  IntegrationPreviewCard,
  IntegrationPreviewCardSkeleton,
} from "./IntegrationPreviewCard"

interface IntegrationSharedInputProps {
  id: string
  field: IntegrationFieldT
  readOnly: boolean
  onFocus: () => void
  onClearItem: () => void
}

interface IntegrationInputProps extends IntegrationSharedInputProps {
  catalogId: string
  itemId?: string
  item?: IntegrationItem
}

export function IntegrationInput(props: IntegrationInputProps) {
  const { itemId, item } = props

  const config = useEditorConfig()

  if (itemId && item && item.id === itemId) {
    return <IntegrationInputLocal {...props} value={item.title} />
  }

  if (itemId && config.integrationFields) {
    return (
      <ErrorBoundary
        key={itemId}
        renderError={() => <IntegrationInputLocal {...props} value={itemId} />}
      >
        <Suspense
          key={itemId}
          fallback={<IntegrationInputLocal {...props} value={itemId} />}
        >
          <IntegrationInputWithTitle {...props} itemId={itemId} />
        </Suspense>
      </ErrorBoundary>
    )
  }

  if (itemId && !config.integrationFields) {
    return <IntegrationInputLocal {...props} value={itemId} />
  }

  return <IntegrationInputLocal {...props} value="" />
}

interface IntegrationFooterProps {
  catalogId: string
  itemId?: string
  item?: IntegrationItem
}

export function IntegrationInputFooter(props: IntegrationFooterProps) {
  const { catalogId, itemId, item } = props

  const config = useEditorConfig()

  if (itemId && item && item.id === itemId) {
    return (
      <FieldFooter>
        <IntegrationPreviewCard catalogId={catalogId} item={item} />
      </FieldFooter>
    )
  }

  if (itemId && config.integrationFields) {
    return (
      <ErrorBoundary key={itemId} renderError={() => null}>
        <FieldFooter>
          <Suspense key={itemId} fallback={<IntegrationPreviewCardSkeleton />}>
            <IntegrationPreview catalogId={catalogId} itemId={itemId} />
          </Suspense>
        </FieldFooter>
      </ErrorBoundary>
    )
  }

  return null
}

interface IntegrationPreviewProps {
  itemId: string
  catalogId: string
}

function IntegrationPreview(props: IntegrationPreviewProps) {
  const { itemId, catalogId } = props

  const { item } = useIntegrationCatalogRead({
    catalogId,
    itemId,
  })

  return <IntegrationPreviewCard catalogId={catalogId} item={item} />
}

interface IntegrationInputLocalProps extends IntegrationSharedInputProps {
  value: string
}

function IntegrationInputLocal(props: IntegrationInputLocalProps) {
  const { id, field, readOnly, value, onFocus, onClearItem } = props

  return (
    <FieldTextInput
      id={id}
      placeholder={getFieldPlaceholder(field)}
      readOnly
      variant="link"
      value={value}
      {...(!readOnly && {
        cursor: "pointer",
        onFocus,
      })}
      endAdornment={
        <Button
          startIcon="close"
          color="grey"
          size="small"
          disabled={!value}
          onClick={onClearItem}
        />
      }
    />
  )
}

interface IntegrationInputWithTitleProps extends IntegrationSharedInputProps {
  itemId: string
  catalogId: string
}

function IntegrationInputWithTitle(props: IntegrationInputWithTitleProps) {
  const { itemId, catalogId } = props

  const { item } = useIntegrationCatalogRead({
    catalogId,
    itemId,
  })

  return <IntegrationInputLocal {...props} value={item.title} />
}
